import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { ListDetailManagementService } from '@app/lists/shared/list-detail-management/services/list-detail-management.service';
import { Store } from '@ngrx/store';
import {
  displayListDetailsTransformer,
  ListConstants,
  ListTracingConstants,
  MASTER_LIST_ACTIONS,
  MasterListState,
  selectMasterListItemState,
  selectMasterListPrimaryCountsMap,
} from '@usf/ngrx-list';
import { ProductPricingService } from '@usf/ngrx-pricing';
import { getProductAlternates, ProductStateService } from '@usf/ngrx-product';
import { Product } from '@usf/product-types';
import { combineLatest, Observable } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { State } from 'src/app/store';
import { PATHS } from '../../../../shared/constants/paths';
import { NavigationHelperService } from '../../../../shared/helpers/navigation.helpers.service';
import { ProductService } from '../../../../shared/services/product/product.service';
import {
  selectMasterListHeader,
  selectMasterListStatesAreLoaded,
} from '../selectors/master-list.selectors';
import {
  NR_CUSTOMATTRIBUTE_SHOPPING_LIST_ID,
  NR_CUSTOMATTRIBUTE_SHOPPING_LIST_TYPE,
  NR_CUSTOMATTRIBUTE_SHOPPING_LIST_NUM_PRODUCTS,
} from '../../../../shared/constants/newrelic-customattribute-constants';

@Injectable({
  providedIn: 'root',
})
export class MasterListResolverService {
  constructor(
    private listDetailManagementService: ListDetailManagementService,
    private store: Store<State>,
    private productPricingService: ProductPricingService,
    private productService: ProductService,
    private productStateService: ProductStateService,
    private navigationHelperService: NavigationHelperService,
  ) {}

  resolve(route: ActivatedRouteSnapshot): any {
    const listKey = this.listDetailManagementService.getListIdFromRoute(route);
    const listId = Number(listKey.listId);
    return this.getProductsAndPricingForMasterListProducts(listId);
  }

  getProductsAndPricingForMasterListProducts(listId: number) {
    const masterListItemProductState = this.getMasterListItemProductState();
    const masterListStatesAreLoaded = this.getMasterListsAreLoaded();
    const masterListHeader = this.getMasterListHeader(listId);
    const primaryCountsProductMap = this.getPrimaryCountsProductMap();
    return combineLatest([
      masterListItemProductState,
      masterListStatesAreLoaded,
      masterListHeader,
      primaryCountsProductMap,
    ]).pipe(
      filter(
        ([
          mslItemProductState,
          isLoaded,
          masterListHeader,
          primaryCountsProductMap,
        ]) => {
          if (!masterListHeader && isLoaded) {
            this.navigationHelperService.routeTo(PATHS.LISTS, {
              getChanges: false,
            });
          }
          return (
            isLoaded && primaryCountsProductMap.has(masterListHeader?.listId)
          );
        },
      ),
      tap(
        ([
          mslItemProductState,
          isLoaded,
          masterListHeader,
          primaryCountsProductMap,
        ]) => {
          this.listDetailManagementService.displayListDetailsStartTrace(
            ListConstants.masterListTypeId,
            String(masterListHeader.listId),
            primaryCountsProductMap.get(masterListHeader.listId),
          );
          const newrelic = window['newrelic'];
          try {
            if (!!newrelic) {
              newrelic.setCustomAttribute(
                NR_CUSTOMATTRIBUTE_SHOPPING_LIST_ID,
                String(masterListHeader.listId),
              );
              newrelic.setCustomAttribute(
                NR_CUSTOMATTRIBUTE_SHOPPING_LIST_TYPE,
                ListConstants.masterListTypeId,
              );
              newrelic.setCustomAttribute(
                NR_CUSTOMATTRIBUTE_SHOPPING_LIST_NUM_PRODUCTS,
                primaryCountsProductMap.get(masterListHeader.listId),
              );
            }
          } catch (err) {
            console.log('Error in open master list detail new relic event');
            console.log(err);
          }
          const productNumbers: number[] = [];
          // Collect product numbers from master list items on this list
          mslItemProductState.ids.forEach(id => {
            const masterListItemProduct = mslItemProductState.entities[id];
            if (
              masterListItemProduct &&
              masterListItemProduct.listId === listId
            ) {
              productNumbers.push(masterListItemProduct.productNumber);
            }
          });
          // Get products and prices for msl products
          this.productStateService.loadProductsWithRetry(productNumbers);
          this.productPricingService.getPrices(productNumbers);

          // Get prices for msl substitutes
          this.productService
            .getProducts(productNumbers)
            .pipe(take(1))
            .subscribe(products => {
              this.getPricingForSubstituteProducts(products);
            });
        },
      ),
    );
  }

  getPricingForSubstituteProducts(products: Map<number, Product>) {
    const productArr = Array.from(products.values()).reduce((list, product) => {
      list.push({
        productNumber: product?.productNumber,
        summary: product?.summary,
        inventory: product?.inventory,
      } as Product);
      return list;
    }, new Array<Product>());
    const alternateProductNumbers = getProductAlternates(productArr);
    this.productPricingService.getPrices(alternateProductNumbers);
  }

  getMasterListsAreLoaded() {
    return this.store.select(selectMasterListStatesAreLoaded());
  }

  getMasterListItemProductState() {
    return this.store.select(selectMasterListItemState);
  }

  getMasterListHeader(listId: number) {
    return this.store.select(selectMasterListHeader(listId));
  }

  getPrimaryCountsProductMap() {
    return this.store.select(selectMasterListPrimaryCountsMap);
  }
}
